import { useRef, useState } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { ShieldCheck, Loader2, Check, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { auth, firebaseEnabled } from "@/lib/firebase";
import { otpApi } from "@/lib/endpoints";
import { PhoneInput } from "@/components/PhoneInput";

// Mandatory SMS phone verification. Reports the Firebase ID token via onVerified(token).
// While unverified, the parent should keep its publish button disabled.
export function PhoneVerify({ phone, onPhoneChange, verified, onVerified, onReset, testid = "verify" }) {
  const { t } = useTranslation();
  const [step, setStep] = useState("phone"); // phone | code | done
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const confirmRef = useRef(null);
  const recaptchaRef = useRef(null);
  const containerId = `recaptcha-${testid}`;

  const clearRecaptcha = () => { try { recaptchaRef.current?.clear(); } catch (e) {} recaptchaRef.current = null; };

  const sendCode = async () => {
    if (!phone || phone.replace(/\D/g, "").length < 8) { toast.error(t("phone.errInvalidPhone")); return; }
    if (!firebaseEnabled) { toast.error(t("phone.errSmsUnavailable")); return; }
    setLoading(true);
    try {
      await otpApi.request(phone); // backend OTP rate limit (per phone + IP)
      if (!recaptchaRef.current) {
        recaptchaRef.current = new RecaptchaVerifier(auth, containerId, { size: "invisible" });
      }
      confirmRef.current = await signInWithPhoneNumber(auth, phone, recaptchaRef.current);
      setStep("code");
      toast.success(t("phone.codeSentToast"));
    } catch (e) {
      clearRecaptcha();
      if (e?.response?.status === 429) toast.error(t("phone.errTooMany"));
      else if (String(e?.code || "").includes("invalid-phone")) toast.error(t("phone.errInvalidFormat"));
      else toast.error(t("phone.errSendFailed"));
    } finally { setLoading(false); }
  };

  const verify = async () => {
    if (!confirmRef.current || code.replace(/\D/g, "").length < 6) { toast.error(t("phone.errWriteCode")); return; }
    setLoading(true);
    try {
      const cred = await confirmRef.current.confirm(code.trim());
      const token = await cred.user.getIdToken();
      onVerified(token);
      setStep("done");
      toast.success(t("phone.verifiedToast"));
    } catch (e) {
      toast.error(t("phone.errWrongCode"));
    } finally { setLoading(false); }
  };

  const changeNumber = () => {
    setStep("phone"); setCode(""); confirmRef.current = null; clearRecaptcha(); onReset();
  };

  const btn = "rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2 disabled:opacity-60";

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 flex flex-col gap-2" data-testid={`${testid}-block`}>
      <div id={containerId} />
      <p className="text-sm font-semibold text-neutral-900 flex items-center gap-1.5">
        <ShieldCheck className="w-4 h-4 text-[#0066CC]" /> {t("phone.verifyTitle")}
      </p>

      {step === "done" || verified ? (
        <div className="flex items-center justify-between gap-2" data-testid={`${testid}-done`}>
          <span className="text-sm text-[#166534] font-medium flex items-center gap-1"><Check className="w-4 h-4" /> {t("phone.verified")} {phone}</span>
          <button type="button" onClick={changeNumber} className="text-xs text-[#0066CC] hover:underline" data-testid={`${testid}-change`}>{t("phone.changeNumber")}</button>
        </div>
      ) : step === "phone" ? (
        <>
          <PhoneInput testid={`${testid}-phone`} value={phone} onChange={onPhoneChange} placeholder={t("phone.phonePlaceholder")} />
          <button type="button" onClick={sendCode} disabled={loading} data-testid={`${testid}-send`}
            className={`${btn} bg-[#0066CC] text-white hover:bg-[#0052A3]`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><MessageSquare className="w-4 h-4" /> {t("phone.sendCode")}</>}
          </button>
        </>
      ) : (
        <>
          <p className="text-xs text-neutral-600">{t("phone.codeSent")} <b>{phone}</b>{t("phone.writeHere")}</p>
          <input type="tel" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            data-testid={`${testid}-code`} placeholder={t("phone.codePlaceholder")}
            className="w-full min-h-[48px] rounded-sm border border-neutral-300 bg-white px-3 text-center text-lg tracking-widest outline-none focus:border-[#0066CC]" />
          <div className="flex gap-2">
            <button type="button" onClick={verify} disabled={loading} data-testid={`${testid}-confirm`}
              className={`${btn} flex-1 bg-[#166534] text-white hover:opacity-90`}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("phone.verifyCode")}
            </button>
            <button type="button" onClick={changeNumber} className="px-3 text-sm text-neutral-500 hover:text-neutral-800" data-testid={`${testid}-change`}>{t("phone.changeNumber")}</button>
          </div>
        </>
      )}
    </div>
  );
}
